
import { Fragment, useEffect, useState } from "react";
import { Card, Col, Container, Input, Row } from "reactstrap";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import { useSelector } from "react-redux";
import { RootState } from "../../redux-toolkit/store";
import MonthDropdown from "../CommonCompo/MonthDropdown";
import CommonTableNew, {
  HeadDataItem,
} from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { API } from "../../api/API";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";
import { Image } from "../../AbstractElements";
import { dynamicImage } from "../../Utils";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import { getFilteredPositionIds } from "../../db/DexieServises";

export default function JwReport() {
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  const [searchText, setSearchText] = useState("");
  const [jwData, setJwData] = useState<any[]>([]);
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState<HeadDataItem[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const serverUrl = localStorage.getItem("serverUrl");

  useEffect(() => {
    if (selectedMonth) {
      fetchJwData();
    }
  }, [selectedMonth, unitId, selectedIds]);

  useEffect(() => {
    const fetchPositionIds = async () => {
      if (unitId) {
        const ids = await getFilteredPositionIds("Position", unitId);
        setSelectedIds(ids);
      }
    };

    fetchPositionIds();
  }, [unitId]);

  const fetchJwData = async () => {
    if (!serverUrl) {
      console.error("Server URL is not configured.");
      return;
    }

    setLoading(true);
    const url = `${serverUrl}${API.getJwReport}`;
    const positionIds =
      selectedIds.length > 0
        ? selectedIds
        : await getFilteredPositionIds("Position", unitId);
    const { status, data } = await getData(url, true, {
      moye: selectedMonth,
      position_id: positionIds,
    });

    if (status === 200 && Array.isArray(data?.data)) {
      setJwData(data.data);
      setDisplayedData(data.data);

      // Dynamically set columns based on API response keys
      if (data.data.length > 0) {
        const headerMapping: Record<string, string> = {
          UserName: "JW Reported with",
          JwEmployeeName: "JW Reported by",
          // UserPositionCode: "User Position Code",
          // UserHqName: "User HQ",
          // UserEmpCode: "User Emp Code",
          // JwHqName: "JW HQ Name",
          // JwEmpCode: "JW Emp Code",
          // JwPositionCode: "JW Position Code",
          // NoOfJwDaysWorked: "No Of JW Days Worked",
          // NoOfCallsJw: "No Of Calls JW",
          // MonthYear: "Month-Year",
        };

        const dynamicColumns: HeadDataItem[] = Object.keys(data.data[0]).map(
          (key, index) => ({
            id: String(index + 1),
            head: headerMapping[key] || key.toString(),
            key: key,
            sortKey: key,
          })
        );

        setColumns(dynamicColumns);
      }
    } else {
      setJwData([]);
      setDisplayedData([]);
    }
    setLoading(false);
  };

  const handleExportToExcel = () => {
    const exportData = displayedData.map((row) => {
      const out: Record<string, any> = {};
      columns.forEach((c) => {
        const key = typeof c.head === "string" ? c.head : c.key;
        out[key] = (row as any)[c.key] ?? "-";
      });
      return out;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Jw Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Jw_Report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  useEffect(() => {
    const lower = searchText.toLowerCase();
    const filtered = jwData.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(lower)
      )
    );
    setDisplayedData(filtered);
  }, [searchText, jwData]);

  return (
    <div className="page-body p-2">
      <Card>
        <div className="d-flex justify-content-between align-items-center pt-2 pb-2">
          <CardHeaderSpan
            headingClassName="pb-0"
            back={true}
            heading="Jw Report"
            span="Detailed Jw Report"
          />
          <div className="p-3">
            <MonthDropdown onChange={(value) => setSelectedMonth(value)} />
          </div>
        </div>
        <div className="row px-3 mb-3 align-items-center justify-content-between">
          <div className="col-md-8 d-flex align-items-center gap-3 flex-wrap">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by name or position"
              className="form-control"
              style={{ maxWidth: "250px" }}
            />
            <TreeModel onSelectionChange={setSelectedIds} />
          </div>

          <div className="col-md-4 d-flex justify-content-end gap-3">
            <button
              className="btn btn-sm btn-primary align-items-center d-flex "
              onClick={handleExportToExcel}
            >
              <FeatherIconCom
                iconName={"Download"}
                size={14}
                className="text-light"
              />
              <span className="px-2" style={{ fontSize: 14 }}>
                Export
              </span>
            </button>
          </div>
        </div>

        <Fragment>
          <Container fluid>
            <Row>
              <Col sm={12}>
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-3">
                      <div>
                        <Image
                          className="b-r-10 img-80"
                          src={dynamicImage("dashboard/loader.gif")}
                          alt="Loading..."
                        />
                      </div>
                    </div>
                  ) : (
                    <CommonTableNew
                      caption=""
                      headData={columns}
                      data={displayedData}
                      renderRow={(row, rowIndex) => (
                        <tr key={rowIndex}>
                          {columns.map((c) => (
                            <td key={c.key}>{(row as any)[c.key] ?? "-"}</td>
                          ))}
                        </tr>
                      )}
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </Fragment>
      </Card>
    </div>
  );
}